import { fileUtils } from "@/lib/utils/fileUtils";
import {
  getErrorType,
  getUserFriendlyErrorMessage,
  isRetryableError,
} from "@/components/Chat/messages/utils/errorTypeMapper";
import { create } from "zustand";

const useTempChatStore = create((set, get) => ({
  // State
  messages: [],
  isStreaming: false,
  currentStreamingMessageId: null,
  error: null,
  errorType: "general",
  retryCount: 0,
  maxRetries: 2,
  abortController: null,

  // Model and assistant selection state
  selectedModel: "gpt-4.1-mini",
  selectedProvider: "openai",
  selectedAssistant: null, // Pre-defined assistant template

  // Actions
  sendMessage: async (content) => {
    if (!content?.trim() || get().isStreaming) return;

    const userMessageId = fileUtils.generateUUID();
    const assistantMessageId = fileUtils.generateUUID();

    // Add user message
    const userMessage = {
      id: userMessageId,
      role: "user",
      content: content.trim(),
      timestamp: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
      error: null,
    }));

    // Add initial assistant message (capture model/provider in metadata at send time)
    const assistantMessage = {
      id: assistantMessageId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      metadata: {
        model: get().selectedModel,
        provider: get().selectedProvider,
      },
    };

    set((state) => ({
      messages: [...state.messages, assistantMessage],
      isStreaming: true,
      currentStreamingMessageId: assistantMessageId,
    }));

    // Helper function for exponential backoff
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Retry logic wrapper
    const attemptSend = async (attemptNumber = 0) => {
      try {
        const { streamTempChat } = await import("../services/tempChatService");
        await streamTempChat(content, assistantMessageId);
        set({ retryCount: 0, error: null, errorType: "general" }); // Reset on success
      } catch (error) {
        console.error("Error in temp chat streaming:", error);
        const errorType = getErrorType(error);
        const canRetry = isRetryableError(error);
        const maxRetries = get().maxRetries;

        if (canRetry && attemptNumber < maxRetries) {
          // Calculate exponential backoff: 1s, 2s
          const backoffMs = Math.pow(2, attemptNumber) * 1000;

          console.log(
            `Retrying in ${backoffMs}ms (attempt ${attemptNumber + 1}/${maxRetries})...`
          );

          // Show retry message to user
          set({
            error: `Connection issue detected. Retrying automatically (${attemptNumber + 1}/${maxRetries})...`,
            errorType: "network",
            retryCount: attemptNumber + 1,
          });

          await wait(backoffMs);
          return attemptSend(attemptNumber + 1);
        } else {
          // Final failure or non-retryable error
          // Get user-friendly error message from shared error mapper
          const baseMessage = getUserFriendlyErrorMessage(error);
          const errorContent =
            attemptNumber > 0
              ? `${baseMessage} (after ${attemptNumber} retry attempt${attemptNumber > 1 ? "s" : ""})`
              : baseMessage;

          // First, mark the message as an error with empty content
          set((state) => ({
            error: null, // Clear separate error state (error is now in the message)
            errorType: "general",
            retryCount: attemptNumber,
            currentStreamingMessageId: assistantMessageId, // Keep streaming state for the animation
            messages: state.messages.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content: "", // Start empty, will stream in
                    isError: true, // Mark as error message (Claude pattern)
                    errorType, // Store error type for styling
                    canRetry: isRetryableError(error), // Whether retry is available
                  }
                : msg
            ),
          }));

          // Stream the error message in character by character
          get().streamTextIntoMessage(assistantMessageId, errorContent, () => {
            // When streaming completes, clear streaming state
            set({ isStreaming: false, currentStreamingMessageId: null });
          });
        }
      }
    };

    await attemptSend(0);
  },

  addMessage: (message) => {
    set((state) => ({
      messages: [
        ...state.messages,
        { ...message, id: message.id || fileUtils.generateUUID() },
      ],
    }));
  },

  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    }));
  },

  updateStreamingContent: (messageId, content) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, content } : msg
      ),
    }));
  },

  setStreaming: (isStreaming, messageId = null) => {
    set({
      isStreaming,
      currentStreamingMessageId: messageId,
    });
  },

  setError: (error, errorType = "general", retryCount = 0) => {
    set({ error, errorType, retryCount });
  },

  resetRetryCount: () => {
    set({ retryCount: 0 });
  },

  clearChat: () => {
    // Abort any ongoing stream
    const { abortController } = get();
    if (abortController) {
      abortController.abort();
    }

    set({
      messages: [],
      isStreaming: false,
      currentStreamingMessageId: null,
      error: null,
      errorType: "general",
      retryCount: 0,
      abortController: null,
      // Reset model and assistant to defaults
      selectedModel: "gpt-4.1-mini",
      selectedProvider: "openai",
      selectedAssistant: null,
    });
  },

  setAbortController: (controller) => {
    set({ abortController: controller });
  },

  abortStream: () => {
    const { abortController } = get();
    if (abortController) {
      abortController.abort();
      set({
        isStreaming: false,
        currentStreamingMessageId: null,
        abortController: null,
      });
      return true;
    }
    return false;
  },

  // Stream text into a message character by character (simulates model response)
  // Varies speed slightly to feel more natural like a real streaming response
  streamTextIntoMessage: (messageId, text, onComplete) => {
    let currentIndex = 0;
    let timeoutId = null;

    const streamNextChunk = () => {
      // Vary characters per tick (1-3 chars)
      const charsPerTick = Math.floor(Math.random() * 3) + 1;
      currentIndex += charsPerTick;
      const currentText = text.slice(0, currentIndex);

      set((state) => ({
        messages: state.messages.map((msg) =>
          msg.id === messageId ? { ...msg, content: currentText } : msg
        ),
      }));

      if (currentIndex >= text.length) {
        if (onComplete) onComplete();
        return;
      }

      // Vary the delay between ticks
      // Base delay 15-35ms, with occasional longer pauses after punctuation
      const lastChar = text[currentIndex - 1];
      const isPunctuation = [".", ",", "!", "?", ";"].includes(lastChar);
      const baseDelay = Math.floor(Math.random() * 20) + 15; // 15-35ms
      const delay = isPunctuation ? baseDelay + Math.random() * 80 : baseDelay; // Extra 0-80ms after punctuation

      timeoutId = setTimeout(streamNextChunk, delay);
    };

    // Start streaming
    streamNextChunk();

    // Return cleanup function
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  },

  // Retry from an error message (Claude pattern)
  retryLastMessage: () => {
    const messages = get().messages;
    // Find the last user message
    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.role === "user");
    if (!lastUserMessage) return;

    // Find and remove the error assistant message
    const errorMessageIndex = messages.findIndex(
      (m) => m.role === "assistant" && m.isError
    );
    if (errorMessageIndex !== -1) {
      set((state) => ({
        messages: state.messages.filter((_, i) => i !== errorMessageIndex),
      }));
    }

    // Resend the last user message
    get().sendMessage(lastUserMessage.content);
  },

  // Export messages for conversion to real chat
  exportMessages: () => {
    return get().messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
    }));
  },

  // Import messages (useful for testing or conversion back from real chat)
  importMessages: (messages) => {
    const formattedMessages = messages.map((msg) => ({
      ...msg,
      id: msg.id || fileUtils.generateUUID(),
    }));
    set({ messages: formattedMessages });
  },

  // Model and assistant selection actions
  setSelectedModel: (model, provider) => {
    set({ selectedModel: model, selectedProvider: provider });
  },

  setSelectedAssistant: (assistant) => {
    set({ selectedAssistant: assistant });
  },
}));

export default useTempChatStore;
